import { Show, createEffect, createSignal, type Signal } from "solid-js";

// Persisted signal: reads initial value from localStorage (if present) and
// writes every change back. Lets the tuner survive page reloads, tab closes,
// and dev server restarts. SSR-safe (skips when no window). To start fresh,
// clear the `glow-tuner:*` keys from DevTools → Application → Local Storage.
const STORAGE_PREFIX = "glow-tuner:";

function persistedSignal<T>(key: string, defaultValue: T): Signal<T> {
  // In prod, the GLOW.* gates resolve to the baked-in constants and the
  // signal getters/setters are never called. Skip the createSignal /
  // createEffect / localStorage code entirely so Rollup can strip the
  // signal infrastructure from the prod bundle.
  if (!import.meta.env.DEV) {
    return [() => defaultValue, (_: T) => defaultValue] as unknown as Signal<T>;
  }
  let initial = defaultValue;
  if (typeof window !== "undefined") {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (raw !== null) {
      try {
        initial = JSON.parse(raw) as T;
      } catch {
        /* fall back to default */
      }
    }
  }
  const [get, set] = createSignal<T>(initial);
  createEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(get()));
    } catch {
      /* quota or disabled */
    }
  });
  return [get, set];
}

const STROKE_BASE = /* static prod value */ 4;
const STROKE_PER_STEP = /* static prod value */ 0.3;
const GAP_LENGTH = /* static prod value */ 2;
const SPEED = /* static prod value */ 56;
const CHALLENGE_SPEED_MULT = /* static prod value */ 4.4;
const ROUND_CAPS = /* static prod value */ false;
const INSET_PX = /* static prod value */ 12;
const TIER_OFFSET = /* static prod value */ 2;
const BLUR_BASE = /* static prod value */ 5;
const BLUR_PER_STEP = /* static prod value */ 3;
const OPACITY_BASE = /* static prod value */ 0.4;
const OPACITY_PER_STEP = /* static prod value */ 0.12;
const OPACITY_MAX = /* static prod value */ 1;
const SHADOW_BLUR_BASE = /* static prod value */ 12;
const SHADOW_BLUR_PER_STEP = /* static prod value */ 0.5;
const SHADOW_OPACITY_BASE = /* static prod value */ 0.4;
const SHADOW_OPACITY_PER_STEP = /* static prod value */ 0.15;
const SHADOW_OPACITY_MAX = /* static prod value */ 0.8;

// ── Shared signals - read by driver-card to render the SVG stroke ────────────
// Geometry of the dash pattern on the perimeter. We use pathLength=100 on the
// rect, so all values are in percent-of-perimeter units regardless of the
// card's actual width.

// ── Stroke / dash geometry ──────────────────────────────────────────────────
// Stroke thickness in CSS px (the perpendicular width of the orbiting band).
// Scales with tier intensity: width = base + intensity * per_step, matching
// the wrapper-blur and shadow-blur scaling pattern.
const [glowStrokeBase, setGlowStrokeBase] = persistedSignal(
  "strokeBase",
  STROKE_BASE,
);
const [glowStrokePerStep, setGlowStrokePerStep] = persistedSignal(
  "strokePerStep",
  STROKE_PER_STEP,
);

// Length of the gap *between* dashes, as % of perimeter. Dash length is what's
// left of the slot (100/N), so widening the gap shortens the dashes.
const [glowGapLength, setGlowGapLength] = persistedSignal(
  "gapLength",
  GAP_LENGTH,
);

// One full orbit, in seconds
const [glowSpeed, setGlowSpeed] = persistedSignal("speed", SPEED);

// Factor multiplied into animation speed when the challenge button is hovered
// (1 = no change, 2 = orbit at double speed).
const [glowChallengeSpeedMult, setGlowChallengeSpeedMult] =
  persistedSignal("challengeSpeedMult", CHALLENGE_SPEED_MULT);

// stroke-linecap: round dashes vs butt (square) ends
const [glowRoundCaps, setGlowRoundCaps] = persistedSignal(
  "roundCaps",
  ROUND_CAPS,
);

// ── Wrapper / blur / opacity ─────────────────────────────────────────────────
// How far the wrapper extends past the card edge (headroom for stroke + blur)
const [glowInsetPx, setGlowInsetPx] = persistedSignal(
  "insetPx",
  INSET_PX,
);

// Tier intensity threshold - `max(TIER_ORDER[tier] - offset, 0)`. Higher offset
// means more tiers are glow-free at the low end. 1 = white/yellow have no glow.
const [glowTierOffset, setGlowTierOffset] = persistedSignal(
  "tierOffset",
  TIER_OFFSET,
);

// Blur radius applied to the wrapper. Computed blur = base + intensity * per_step.
const [glowBlurBase, setGlowBlurBase] = persistedSignal(
  "blurBase",
  BLUR_BASE,
);
const [glowBlurPerStep, setGlowBlurPerStep] = persistedSignal(
  "blurPerStep",
  BLUR_PER_STEP,
);

// Opacity envelope. opacity = min(base + intensity * per_step, max), multiplied
// by --glow-mult (resting / hover / challenge).
const [glowOpacityBase, setGlowOpacityBase] = persistedSignal(
  "opacityBase",
  OPACITY_BASE,
);
const [glowOpacityPerStep, setGlowOpacityPerStep] = persistedSignal(
  "opacityPerStep",
  OPACITY_PER_STEP,
);
const [glowOpacityMax, setGlowOpacityMax] = persistedSignal(
  "opacityMax",
  OPACITY_MAX,
);

// ── Static box-shadow halo behind the card ───────────────────────────────────
// Soft tier-colored shadow around the card itself, complements the orbiting
// stroke. No offset, no animation - just a static aura. Blur and opacity
// scale with tier intensity.
const [glowShadowBlurBase, setGlowShadowBlurBase] = persistedSignal(
  "shadowBlurBase",
  SHADOW_BLUR_BASE,
);
const [glowShadowBlurPerStep, setGlowShadowBlurPerStep] =
  persistedSignal("shadowBlurPerStep", SHADOW_BLUR_PER_STEP);
const [glowShadowOpacityBase, setGlowShadowOpacityBase] =
  persistedSignal("shadowOpacityBase", SHADOW_OPACITY_BASE);
const [glowShadowOpacityPerStep, setGlowShadowOpacityPerStep] =
  persistedSignal("shadowOpacityPerStep", SHADOW_OPACITY_PER_STEP);
const [glowShadowOpacityMax, setGlowShadowOpacityMax] = persistedSignal(
  "shadowOpacityMax",
  SHADOW_OPACITY_MAX,
);

// Consumer-facing exports - these are what driver-card and any other glow
// consumer should import. In dev the gate resolves to the signal getter (live
// tuning). In prod it resolves to `() => <constant>`, and Vite/Rollup DCE
// strips the dev branch + all signal infrastructure from the bundle.
//
// The `/* static prod value */ <N>` comment is the anchor the Vite plugin
// `glow-tuner-writeback` (vite.config.ts) looks for when writing back from the
// dev tuner's "Save to source" button. Edit by tuner, not by hand.
export const GLOW = {
  STROKE_BASE: import.meta.env.DEV ? glowStrokeBase : () => STROKE_BASE,
  STROKE_PER_STEP: import.meta.env.DEV
    ? glowStrokePerStep
    : () => STROKE_PER_STEP,
  GAP_LENGTH: import.meta.env.DEV ? glowGapLength : () => GAP_LENGTH,
  SPEED: import.meta.env.DEV ? glowSpeed : () => SPEED,
  CHALLENGE_SPEED_MULT: import.meta.env.DEV
    ? glowChallengeSpeedMult
    : () => CHALLENGE_SPEED_MULT,
  ROUND_CAPS: import.meta.env.DEV ? glowRoundCaps : () => ROUND_CAPS,
  INSET_PX: import.meta.env.DEV ? glowInsetPx : () => INSET_PX,
  TIER_OFFSET: import.meta.env.DEV ? glowTierOffset : () => TIER_OFFSET,
  BLUR_BASE: import.meta.env.DEV ? glowBlurBase : () => BLUR_BASE,
  BLUR_PER_STEP: import.meta.env.DEV ? glowBlurPerStep : () => BLUR_PER_STEP,
  OPACITY_BASE: import.meta.env.DEV ? glowOpacityBase : () => OPACITY_BASE,
  OPACITY_PER_STEP: import.meta.env.DEV
    ? glowOpacityPerStep
    : () => OPACITY_PER_STEP,
  OPACITY_MAX: import.meta.env.DEV ? glowOpacityMax : () => OPACITY_MAX,
  SHADOW_BLUR_BASE: import.meta.env.DEV
    ? glowShadowBlurBase
    : () => SHADOW_BLUR_BASE,
  SHADOW_BLUR_PER_STEP: import.meta.env.DEV
    ? glowShadowBlurPerStep
    : () => SHADOW_BLUR_PER_STEP,
  SHADOW_OPACITY_BASE: import.meta.env.DEV
    ? glowShadowOpacityBase
    : () => SHADOW_OPACITY_BASE,
  SHADOW_OPACITY_PER_STEP: import.meta.env.DEV
    ? glowShadowOpacityPerStep
    : () => SHADOW_OPACITY_PER_STEP,
  SHADOW_OPACITY_MAX: import.meta.env.DEV
    ? glowShadowOpacityMax
    : () => SHADOW_OPACITY_MAX,
};

export type GlowConfig = {
  [key in keyof typeof GLOW]: ReturnType<(typeof GLOW)[key]>;
};

// CSS `stroke-dasharray` value: "<L> <G>" where L is the visible dash and G
// is the user-controlled gap (% of perimeter). Each slot is 100/N, so the
// dash takes whatever's left after the gap; SVG repeats the L-G pair so N
// evenly-spaced dashes tile the perimeter exactly.
export const glowDashArray = (count: number): string => {
  if (count <= 0) return "0 100"; // no visible dashes
  const slot = 100 / count;
  const g = GLOW.GAP_LENGTH();
  const l = Math.max(0, slot - g);
  return `${l} ${g}`;
};

// ── Save-to-source ──────────────────────────────────────────────────────────
// POSTs the current signal values to the Vite middleware (see vite.config.ts
// `glow-tuner-writeback`), which rewrites the `/* static prod value */ N`
// anchors in this file. Dev-only - there's nothing to save in prod.
async function saveToSource(): Promise<{ ok: boolean; message: string }> {
  if (!import.meta.env.DEV)
    return { ok: false, message: "Not available in prod" };
  const payload = {
    STROKE_BASE: glowStrokeBase(),
    STROKE_PER_STEP: glowStrokePerStep(),
    GAP_LENGTH: glowGapLength(),
    SPEED: glowSpeed(),
    CHALLENGE_SPEED_MULT: glowChallengeSpeedMult(),
    ROUND_CAPS: glowRoundCaps(),
    INSET_PX: glowInsetPx(),
    TIER_OFFSET: glowTierOffset(),
    BLUR_BASE: glowBlurBase(),
    BLUR_PER_STEP: glowBlurPerStep(),
    OPACITY_BASE: glowOpacityBase(),
    OPACITY_PER_STEP: glowOpacityPerStep(),
    OPACITY_MAX: glowOpacityMax(),
    SHADOW_BLUR_BASE: glowShadowBlurBase(),
    SHADOW_BLUR_PER_STEP: glowShadowBlurPerStep(),
    SHADOW_OPACITY_BASE: glowShadowOpacityBase(),
    SHADOW_OPACITY_PER_STEP: glowShadowOpacityPerStep(),
    SHADOW_OPACITY_MAX: glowShadowOpacityMax(),
  } satisfies GlowConfig;
  try {
    const res = await fetch("/__glow-tuner/save", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = (await res.json()) as {
      written?: string[];
      errors?: string[];
      error?: string;
    };
    if (!res.ok)
      return { ok: false, message: data.error ?? `HTTP ${res.status}` };
    if (data.errors?.length)
      return {
        ok: false,
        message: `Wrote ${data.written?.length ?? 0}; errors: ${data.errors.join(", ")}`,
      };
    return {
      ok: true,
      message: `Wrote ${data.written?.length ?? 0} value(s) to source`,
    };
  } catch (e) {
    return { ok: false, message: String(e) };
  }
}

// ── UI ──────────────────────────────────────────────────────────────────────
// Renders only in dev. Add <GlowTuner /> once in the tree (root layout is
// ideal; rendering it inside a card that mounts multiple times will stack).
export function GlowTuner() {
  /* DISABLED */
  if (true || !import.meta.env.DEV) return null;

  const [collapsed, setCollapsed] = createSignal(true);
  const [saveState, setSaveState] = createSignal<{
    kind: "idle" | "saving" | "ok" | "err";
    message?: string;
  }>({ kind: "idle" });

  const handleSave = async () => {
    setSaveState({ kind: "saving" });
    const result = await saveToSource();
    setSaveState({ kind: result.ok ? "ok" : "err", message: result.message });
    if (result.ok) {
      setTimeout(() => setSaveState({ kind: "idle" }), 3000);
    } else {
      // Loud + sticky: a failed save silently lost hours of tuning once already.
      // No auto-dismiss until the user clicks the X, and a console.error for the
      // browser devtools so even a closed tuner still flags it.
      console.error("[glow-tuner save]", result.message);
    }
  };

  return (
    <div class="fixed bottom-20 md:bottom-4 left-4 z-[100] bg-black/90 backdrop-blur rounded-lg border border-white/20 text-white text-xs font-mono w-80 select-text flex flex-col">
      <button
        class="flex items-center justify-between p-3 cursor-pointer"
        onClick={(e) => (
          e.stopPropagation(), e.preventDefault(), setCollapsed((c) => !c)
        )}
      >
        <span class="font-bold">Glow Tuner</span>
        <div class="text-white/60 hover:text-white px-2 -my-1 leading-none">
          {collapsed() ? "▸" : "▾"}
        </div>
      </button>

      <Show when={!collapsed()}>
        <div class="space-y-1.5 mt-2 p-3">
          <div class="opacity-70 text-[10px] uppercase tracking-wider">
            Stroke & dashes
          </div>
          <Slider
            label="Stroke base (px)"
            value={glowStrokeBase}
            set={setGlowStrokeBase}
            min={0}
            max={40}
            step={1}
          />
          <Slider
            label="Stroke / tier"
            value={glowStrokePerStep}
            set={setGlowStrokePerStep}
            min={0}
            max={5}
            step={0.1}
          />
          <Slider
            label="Gap (%)"
            value={glowGapLength}
            set={setGlowGapLength}
            min={0}
            max={99}
            step={1}
          />
          <Slider
            label="Speed (s/orbit)"
            value={glowSpeed}
            set={setGlowSpeed}
            min={0.5}
            max={60}
            step={0.5}
          />
          <Slider
            label="Challenge speed"
            value={glowChallengeSpeedMult}
            set={setGlowChallengeSpeedMult}
            min={1}
            max={6}
            step={0.1}
          />

          <label class="flex items-center gap-2 cursor-pointer pt-1 hover:text-white/90">
            <input
              type="checkbox"
              checked={glowRoundCaps()}
              onChange={(e) => setGlowRoundCaps(e.currentTarget.checked)}
            />
            <span>Round caps</span>
          </label>

          <div class="opacity-70 text-[10px] uppercase tracking-wider pt-2">
            Wrapper & blur
          </div>
          <Slider
            label="Inset (px)"
            value={glowInsetPx}
            set={setGlowInsetPx}
            min={0}
            max={60}
            step={1}
          />
          <Slider
            label="Blur base (px)"
            value={glowBlurBase}
            set={setGlowBlurBase}
            min={0}
            max={60}
            step={1}
          />
          <Slider
            label="Blur / tier"
            value={glowBlurPerStep}
            set={setGlowBlurPerStep}
            min={0}
            max={5}
            step={0.1}
          />

          <div class="opacity-70 text-[10px] uppercase tracking-wider pt-2">
            Stroke opacity envelope
          </div>
          <Slider
            label="Str. opacity base"
            value={glowOpacityBase}
            set={setGlowOpacityBase}
            min={0}
            max={1}
            step={0.05}
          />
          <Slider
            label="Str. opacity/tier"
            value={glowOpacityPerStep}
            set={setGlowOpacityPerStep}
            min={0}
            max={0.5}
            step={0.01}
          />
          <Slider
            label="Str. opacity max"
            value={glowOpacityMax}
            set={setGlowOpacityMax}
            min={0}
            max={1}
            step={0.05}
          />

          <div class="opacity-70 text-[10px] uppercase tracking-wider pt-2">
            Box-shadow halo
          </div>
          <Slider
            label="Shadow blur (px)"
            value={glowShadowBlurBase}
            set={setGlowShadowBlurBase}
            min={0}
            max={80}
            step={1}
          />
          <Slider
            label="Shadow / tier"
            value={glowShadowBlurPerStep}
            set={setGlowShadowBlurPerStep}
            min={0}
            max={10}
            step={0.5}
          />
          <Slider
            label="Sh. opacity base"
            value={glowShadowOpacityBase}
            set={setGlowShadowOpacityBase}
            min={0}
            max={1}
            step={0.05}
          />
          <Slider
            label="Sh. opacity/tier"
            value={glowShadowOpacityPerStep}
            set={setGlowShadowOpacityPerStep}
            min={0}
            max={0.5}
            step={0.01}
          />
          <Slider
            label="Sh. opacity max"
            value={glowShadowOpacityMax}
            set={setGlowShadowOpacityMax}
            min={0}
            max={1}
            step={0.05}
          />

          <div class="opacity-70 text-[10px] uppercase tracking-wider pt-2">
            Tier intensity
          </div>
          <Slider
            label="Offset"
            value={glowTierOffset}
            set={setGlowTierOffset}
            min={0}
            max={9}
            step={1}
          />

          <div class="pt-2 text-[10px] opacity-60 break-all leading-relaxed space-y-1">
            <div>dasharray (at N=1) = "{glowDashArray(1)}"</div>
          </div>

          <div class="pt-2 flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={saveState().kind === "saving"}
              class="bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed border border-white/20 rounded px-3 py-1 text-xs font-semibold"
            >
              {saveState().kind === "saving" ? "Saving…" : "Save to source"}
            </button>
            <Show when={saveState().kind === "ok"}>
              <span class="text-[10px] text-green-400">
                {saveState().message}
              </span>
            </Show>
          </div>
          <Show when={saveState().kind === "err"}>
            <div class="mt-2 p-2 bg-red-950/50 border border-red-500/50 rounded text-[11px] text-red-200 leading-snug flex items-start gap-2">
              <span class="flex-1 break-words">{saveState().message}</span>
              <button
                onClick={() => setSaveState({ kind: "idle" })}
                class="text-red-200/70 hover:text-red-100 px-1 -my-1 shrink-0 leading-none"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>
          </Show>
        </div>
      </Show>
    </div>
  );
}

function Slider(props: {
  label: string;
  value: () => number;
  set: (n: number) => void;
  min: number;
  max: number;
  step: number;
}) {
  return (
    <div class="flex items-center gap-2">
      <span class="w-28 opacity-70 shrink-0">{props.label}</span>
      <input
        type="range"
        min={props.min}
        max={props.max}
        step={props.step}
        value={props.value()}
        onInput={(e) => props.set(parseFloat(e.currentTarget.value))}
        class="flex-1 min-w-0"
      />
      <span class="w-12 text-right tabular-nums">
        {props.value().toFixed(props.step < 1 ? 1 : 0)}
      </span>
    </div>
  );
}
